import { Box, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { PaginatedData, useServerSidePagination } from "../../../hooks/PaginationHooks";
import { CircularIndeterminate } from "../../../components/common/CircularIndeterminate";
import { BasicTableRowStyles, MainContentWrapper, TableHeaderWrapper, TableWrapper } from "../../../components/common/Common.styles";
import PageTitle from "../../../components/common/PageTitle/PageTitle";
import { ServerSidePagination } from "../../../components/common/table/ServerSidePagination";
import { ActionModuleAdminListResponse } from "../../../types/actionModule/actionModule";
import ServerSideTableColumnHeader from "../../../components/common/table/ServerSideTableColumnHeader";
import BorderRow from "../../../components/common/table/BorderRow";
import CellBox from "../../../components/common/table/CellBox";
import { useProcessingRequest } from "../../../store/selectors/tableManagementSelector";
import { actionModulesApi } from "../../../api/actionsModulesApi";
import { SearchBarWrapper } from "../../../components/common/stylings/SearchBarWrapper.styles";
import ServerSideSearchBar from "../../../components/common/table/ServerSideSearchBar";
import { GeneralButton } from "../../../common/ui/GeneralButton";
import { EditIcon } from "../../../assets/icons/Icons";
import { Link, useNavigate } from "react-router-dom";
import { useLocale } from "../../../hooks/UseLocale";
import { SearchBarButtonContainerStyling } from "../../../components/common/table/TableElements.styles";
import { companyManagementApi } from "../../../api/companyManagementApi";
import { CompanyListItem } from "../../../types/companyManagementTypes";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export default function ActionModuleListPage() {
    const breadcrumbs = [
        {
            title: "superAdminPage.title",
            link: "superadminarea",
        },
    ];

    const { t } = useTranslation();

    const processingRequest = useProcessingRequest()
    const paginatedData = useServerSidePagination<ActionModuleAdminListResponse>(
        async (paginationParameters) => {
            const res = await actionModulesApi.adminGetAllActionModules(paginationParameters);
            return res?.getResultOrNull();
        },
        []
    );

    return <>
        <PageTitle title={t("actionModuleEditor.allActionModules")} breadcrumbs={breadcrumbs} />

        <MainContentWrapper>
            <SearchBarWrapper>
                <ServerSideSearchBar onSearchHandler={paginatedData.handleOnSearch} />
                <CreateActionModuleButton />
            </SearchBarWrapper>

            <TableWrapper
                minWidth="1000px"
                columnsTemplate="1fr 1fr 1fr 0.5fr 150px"
            >
                <TableHeader
                    paginatedData={paginatedData} />

                <Box sx={{ display: "contents" }}>
                    {paginatedData.items.map((item) =>
                        <TableRow
                            key={item.id}
                            row={item}
                        />)
                    }
                </Box>
            </TableWrapper>
            <ServerSidePagination paginatedData={paginatedData} />
        </MainContentWrapper>
        <CircularIndeterminate processingRequest={processingRequest} />
    </>;
}

function CreateActionModuleButton() {
    const { t } = useTranslation();
    const locale = useLocale();
    const navigate = useNavigate();

    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [companies, setCompanies] = useState<CompanyListItem[]>([]);

    useEffect(() => {
        companyManagementApi.getAllCompanies()
            .then((res) => setCompanies(res.getResultOrNull() ?? []));
    }, []);

    return (
        <SearchBarButtonContainerStyling>
            <Tooltip title={t("toolTip.addNewItem")}>
                <GeneralButton
                    appVariant="icon"
                    onClick={(e) => setAnchor(e.currentTarget)}
                >
                    <AddIcon />
                </GeneralButton>
            </Tooltip>
            <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={() => setAnchor(null)}
                slotProps={{ paper: { sx: { maxHeight: 300 } } }}
            >
                {companies.map((company) =>
                    <MenuItem
                        key={company.id}
                        onClick={() => navigate(`/${locale}/companies/${company.id}/actionmodules/new`)}
                    >
                        {company.name}
                    </MenuItem>
                )}
            </Menu>
        </SearchBarButtonContainerStyling>
    );
};

interface TableHeaderProps {
    paginatedData: PaginatedData<ActionModuleAdminListResponse>;
}

function TableHeader(props: TableHeaderProps) {
    const { t } = useTranslation();

    return (
        <TableHeaderWrapper>
            <ServerSideTableColumnHeader
                fieldName="companyName"
                firstCol
                isSorted
                label={t("tableColumnHeaders.companyName")}
                paginatedData={props.paginatedData}
            />
            <ServerSideTableColumnHeader
                fieldName="name"
                isSorted
                label={t("tableColumnHeaders.name")}
                paginatedData={props.paginatedData}
            />
            <ServerSideTableColumnHeader
                fieldName="description"
                label={t("tableColumnHeaders.description")}
                paginatedData={props.paginatedData}
            />
            <ServerSideTableColumnHeader
                fieldName="rewardType"
                label={t("actionModuleEditor.tabRewardCalculation.rewardType")}
                paginatedData={props.paginatedData}
            />
            <ServerSideTableColumnHeader
                fieldName="actions"
                isSorted={false}
                label=""
                paginatedData={props.paginatedData}
            />
            <BorderRow />
        </TableHeaderWrapper>
    );
};

interface TableRowProps {
    row: ActionModuleAdminListResponse;
}

function TableRow(props: TableRowProps) {
    const locale = useLocale();

    return (
        <BasicTableRowStyles>
            <CellBox firstCol>
                <Typography>{props.row.companyName}</Typography>
            </CellBox>
            <CellBox>
                <Typography>{props.row.name}</Typography>
            </CellBox>
            <CellBox>
                <Typography>{props.row.description}</Typography>
            </CellBox>
            <CellBox>
                <Typography>{props.row.rewardType}</Typography>
            </CellBox>
            <CellBox>
                <Link to={`/${locale}/companies/${props.row.companyId}/actionmodules/${props.row.id}/editor`}>
                    <GeneralButton
                        appVariant="icon">
                        <EditIcon />
                    </GeneralButton>
                </Link>
            </CellBox>
            <BorderRow />
        </BasicTableRowStyles>
    );
};
